import { Edit2, Trash2, Search } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../ui/Button";

export interface Treatment {
  id: string;
  name: string;
  price: number;
  duration: number;
  status: "active" | "inactive";
  description?: string;
}

interface TreatmentsPanelProps {
  treatments: Treatment[];
  onTreatmentEdit: (treatment: Treatment) => void;
  onTreatmentDelete: (id: string, name: string) => void;
  isLoading?: boolean;
}

const formatDuration = (minutes: number) => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
};

export const TreatmentsPanel = ({
  treatments,
  onTreatmentEdit,
  onTreatmentDelete,
  isLoading = false,
}: TreatmentsPanelProps) => {
  return (
    <div className="flex-1 rounded-xl lg:border lg:border-gray-200 lg:shadow-sm bg-transparent lg:bg-white overflow-hidden flex flex-col">
      <div className="flex-1 lg:overflow-y-auto relative p-4 lg:p-0">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10 bg-[#885ABB] border-b border-gray-100 hidden lg:table-header-group">
            <tr className="grid grid-cols-12 gap-4 text-sm font-bold text-white tracking-wide px-6 py-4">
              <th className="col-span-4 text-left font-bold">Treatment Name</th>
              <th className="col-span-2 text-left font-bold">Price</th>
              <th className="col-span-2 text-left font-bold">Duration</th>
              <th className="col-span-2 text-center font-bold">Status</th>
              <th className="col-span-2 text-right font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="w-full lg:divide-y lg:divide-gray-100 bg-transparent lg:bg-white flex flex-col lg:table-row-group gap-4 lg:gap-0">
            {isLoading ? (
              <tr className="h-64 w-full flex items-center justify-center lg:table-row">
                <td colSpan={12} className="h-full">
                  <div className="flex h-full w-full items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#885ABB] border-t-transparent"></div>
                  </div>
                </td>
              </tr>
            ) : treatments.length === 0 ? (
              <tr className="flex flex-col h-64 text-gray-400 w-full lg:grid lg:grid-cols-12">
                <td className="col-span-12 w-full h-full flex flex-col items-center justify-center">
                  <Search className="w-12 h-12 mb-3 opacity-20 mx-auto" />
                  <p className="text-lg font-medium text-gray-500">
                    No treatments found
                  </p>
                  <p className="text-sm">Add your first treatment to get started</p>
                </td>
              </tr>
            ) : (
              <>
                {treatments.map((treatment) => (
                  <tr
                    key={treatment.id}
                    className="flex flex-col lg:grid lg:grid-cols-12 gap-4 items-start lg:items-center px-6 py-5 lg:py-4 hover:bg-purple-50/30 transition-colors bg-white rounded-xl lg:rounded-none border border-gray-200 lg:border-0 shadow-sm lg:shadow-none group text-sm w-full relative"
                  >
                    <td className="lg:col-span-4 min-w-0 w-full lg:w-auto">
                      <div className="font-bold lg:font-semibold text-gray-900 lg:text-gray-800 group-hover:text-[#885ABB] transition-colors truncate text-base lg:text-sm">
                        {treatment.name}
                      </div>
                      {treatment.description && (
                        <div className="text-xs text-gray-500 truncate mt-0.5">
                          {treatment.description}
                        </div>
                      )}
                    </td>

                    <td className="lg:col-span-2 font-bold lg:font-medium text-[#885ABB] lg:text-gray-900 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Price</span>
                      <span>£{treatment.price.toFixed(2)}</span>
                    </td>

                    <td className="lg:col-span-2 text-gray-700 w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Duration</span>
                      <span className="font-medium lg:font-normal">
                        {formatDuration(treatment.duration)}
                      </span>
                    </td>

                    <td className="lg:col-span-2 text-center w-full lg:w-auto flex lg:block justify-between items-center border-t border-gray-50 lg:border-0 pt-3 lg:pt-0">
                      <span className="lg:hidden text-xs font-semibold text-gray-400 uppercase">Status</span>
                      <span
                        className={cn(
                          "inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold border",
                          treatment.status === "active"
                            ? "bg-[#E8F5E9] text-[#2E7D32] border-[#A5D6A7]"
                            : "bg-gray-100 text-gray-600 border-gray-200"
                        )}
                      >
                        {treatment.status === "active" ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td className="lg:col-span-2 flex items-center justify-end gap-2 w-full lg:w-auto mt-4 lg:mt-0 pt-4 lg:pt-0 border-t border-gray-100 lg:border-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onTreatmentEdit(treatment)}
                        className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-blue-600 border-blue-200 hover:bg-blue-50 gap-2 font-bold lg:font-semibold"
                      >
                        <Edit2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                        <span className="lg:hidden">Edit</span>
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onTreatmentDelete(treatment.id, treatment.name)}
                        className="flex-1 lg:flex-none h-10 lg:h-8 px-4 lg:px-2 text-red-600 border-red-200 hover:bg-red-50 gap-2 font-bold lg:font-semibold"
                      >
                        <Trash2 size={16} className="lg:w-[15px] lg:h-[15px]" />
                        <span className="lg:hidden">Delete</span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
